import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useUser } from "../../../Home/UserContext";
import { getRoleFromRoleId } from "../../../../utils/roleHelper";

import { getArticles } from "../../../../services/articlesServices";

import {
  BOUTIQUE_UI_STORAGE_KEY,
  DEFAULT_BOUTIQUE_UI,
  PAGE_SIZE,
  buildArticleSearchText,
  canAddMoreToCart,
  collatorFR,
  getCartTotal,
  getCartUnits,
  isOutOfStock,
  normalizeArticle,
  readStorageJson,
  strip,
  writeStorageJson,
} from "../utils/boutiqueUtils";

const REGLEMENTS_PATH = "/commandes-&-reglements";

export function useBoutiqueDashboard() {
  const navigate = useNavigate();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleDrawerOpen, setArticleDrawerOpen] = useState(false);

  const [ui, setUi] = useState(() => {
    const saved = readStorageJson(BOUTIQUE_UI_STORAGE_KEY, null);
    return saved ? { ...DEFAULT_BOUTIQUE_UI, ...saved } : DEFAULT_BOUTIQUE_UI;
  });

  const [debouncedSearch, setDebouncedSearch] = useState(ui.search || "");

  const persistUi = useCallback((next) => {
    setUi(next);
    writeStorageJson(BOUTIQUE_UI_STORAGE_KEY, next);
  }, []);

  const updateUi = useCallback((patch) => {
    setUi((previous) => {
      const shouldResetPage = ["search", "category", "stock", "sort"].some(
        (key) => Object.prototype.hasOwnProperty.call(patch, key)
      );

      const next = {
        ...previous,
        ...patch,
        perPage: shouldResetPage
          ? PAGE_SIZE
          : patch.perPage ?? previous.perPage,
      };

      writeStorageJson(BOUTIQUE_UI_STORAGE_KEY, next);
      return next;
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateUi({
        search: debouncedSearch,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedSearch, updateUi]);

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getArticles();

      const normalized = Array.isArray(data)
        ? data.map(normalizeArticle).filter((article) => article.is_active)
        : [];

      normalized.sort((a, b) => {
        const order =
          Number(a.ordre_article || 999999) -
          Number(b.ordre_article || 999999);

        if (order !== 0) return order;

        return collatorFR.compare(String(a.nom || ""), String(b.nom || ""));
      });

      setArticles(normalized);
    } catch (error) {
      console.error("Erreur chargement boutique :", error);
      toast.error("Erreur lors de la récupération des articles.");
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const categories = useMemo(() => {
    return Array.from(
      new Set(articles.map((article) => article.categorie).filter(Boolean))
    ).sort((a, b) => collatorFR.compare(a, b));
  }, [articles]);

  const filteredArticles = useMemo(() => {
    const q = strip(ui.search);

    let list = [...articles];

    if (q) {
      list = list.filter((article) =>
        strip(buildArticleSearchText(article)).includes(q)
      );
    }

    if (ui.category !== "all") {
      list = list.filter((article) => article.categorie === ui.category);
    }

    if (ui.stock === "available") {
      list = list.filter((article) => !isOutOfStock(article));
    }

    if (ui.stock === "out") {
      list = list.filter((article) => isOutOfStock(article));
    }

    list.sort((a, b) => {
      if (ui.sort === "alpha") {
        return collatorFR.compare(String(a.nom || ""), String(b.nom || ""));
      }

      if (ui.sort === "priceAsc") {
        return Number(a.prix || 0) - Number(b.prix || 0);
      }

      if (ui.sort === "priceDesc") {
        return Number(b.prix || 0) - Number(a.prix || 0);
      }

      const order =
        Number(a.ordre_article || 999999) -
        Number(b.ordre_article || 999999);

      if (order !== 0) return order;

      return collatorFR.compare(String(a.nom || ""), String(b.nom || ""));
    });

    return list;
  }, [articles, ui]);

  const visibleArticles = useMemo(() => {
    return filteredArticles.slice(0, ui.perPage || PAGE_SIZE);
  }, [filteredArticles, ui.perPage]);

  const stats = useMemo(() => {
    const total = articles.length;
    const available = articles.filter((article) => !isOutOfStock(article)).length;
    const outOfStock = articles.filter((article) => isOutOfStock(article)).length;
    const stockable = articles.filter((article) => article.is_stockable).length;

    return {
      total,
      filtered: filteredArticles.length,
      available,
      outOfStock,
      stockable,
      categories: categories.length,
      cartUnits: getCartUnits(cart),
      cartTotal: getCartTotal(cart),
    };
  }, [articles, cart, categories.length, filteredArticles.length]);

  const resetFilters = useCallback(() => {
    setDebouncedSearch("");
    persistUi(DEFAULT_BOUTIQUE_UI);
  }, [persistUi]);

  const loadMore = useCallback(() => {
    persistUi({
      ...ui,
      perPage: (ui.perPage || PAGE_SIZE) + PAGE_SIZE,
    });
  }, [persistUi, ui]);

  const addToCart = useCallback((article) => {
    const normalizedArticle = normalizeArticle(article);

    if (isOutOfStock(normalizedArticle)) {
      toast.warn(`Stock épuisé pour "${normalizedArticle.nom}".`);
      return;
    }

    setCart((previous) => {
      const found = previous.find((item) => item.id === normalizedArticle.id);
      const currentQuantity = found ? Number(found.quantity || 0) : 0;

      if (!canAddMoreToCart(normalizedArticle, currentQuantity)) {
        toast.warn(`Stock insuffisant pour "${normalizedArticle.nom}".`);
        return previous;
      }

      if (found) {
        return previous.map((item) =>
          item.id === normalizedArticle.id
            ? {
                ...item,
                quantity: Number(item.quantity || 0) + 1,
              }
            : item
        );
      }

      return [
        ...previous,
        {
          ...normalizedArticle,
          quantity: 1,
          message: "",
        },
      ];
    });

    toast.success(`"${normalizedArticle.nom}" ajouté au panier.`);
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart((previous) => previous.filter((item) => item.id !== id));
    toast.success("Article retiré du panier.");
  }, []);

  const openArticleDrawer = useCallback((article) => {
    setSelectedArticle(article);
    setArticleDrawerOpen(true);
  }, []);

  const closeArticleDrawer = useCallback(() => {
    setSelectedArticle(null);
    setArticleDrawerOpen(false);
  }, []);

  const openCart = useCallback(() => setCartOpen(true), []);
  const closeCart = useCallback(() => setCartOpen(false), []);

  const goToReglements = useCallback(() => {
    if (!user) {
      toast.error("Veuillez vous connecter pour accéder à cette page.");
      navigate("/login");
      return;
    }

    navigate(REGLEMENTS_PATH);
  }, [navigate, user]);

  return {
    user,
    userRole,

    articles,
    filteredArticles,
    visibleArticles,
    categories,
    loading,
    stats,

    ui,
    debouncedSearch,
    setDebouncedSearch,
    updateUi,
    resetFilters,
    loadMore,

    cart,
    setCart,
    cartOpen,
    openCart,
    closeCart,
    addToCart,
    removeFromCart,

    selectedArticle,
    articleDrawerOpen,
    openArticleDrawer,
    closeArticleDrawer,

    goToReglements,
    refresh: fetchArticles,
  };
}